import { AnyAction } from 'redux';
import { ImportStatus } from '@synergy/models';
import { PaginatedResponse } from '@synergy/api';

import {
    COMPUTE_ITEM_CHANGES_STARTED,
    DELETE_IMPORT,
    IMPORT_ABORTED,
    IMPORT_CHANGE_SETTINGS,
    IMPORT_CHANGE_SETTINGS_PUBLISH_DATE,
    PUBLISH_ARTICLES_COMPLETED,
    RECEIVE_IMPORTS,
    RECEIVE_IMPORT_DETAILS,
    RECEIVE_IMPORT_JOB_ITEMS,
    REMOVE_ARTICLES_COMPLETED,
    REQUEST_COMPUTE_ITEM_CHANGES,
    REQUEST_IMPORTS,
    REQUEST_IMPORTS_END,
    REQUEST_IMPORT_DETAILS,
    REQUEST_IMPORT_JOB_ITEMS,
    REQUEST_RETRY_FAILED,
    RESET_IMPORTS,
    RETRY_FAILED_COMPLETED,
} from './import-actions';
import { ImportItem } from '../../models/import-item';
import { ImportJob } from '../../models/import-job';
import { ImportJobDetail } from '../../models/import-job-detail';
import { ImportSupplier } from '../../models/import-supplier';

export interface ImportReducerState {
    importJobs: PaginatedResponse<ImportJob>;
    importJobItems: PaginatedResponse<ImportItem>;
    requesting: boolean;
    importDetails?: ImportJobDetail;
    importSuppliers: PaginatedResponse<ImportSupplier>;
}

const initialImportsReducerState: ImportReducerState = {
    importJobs: { limit: 30, page: 1, total: 0, items: [] },
    importJobItems: { limit: 30, page: 1, total: 0, items: [] },
    requesting: false,
    importSuppliers: { limit: 30, page: 1, total: 0, items: [] },
};

export const importsReducer = (
    state: ImportReducerState = initialImportsReducerState,
    action: AnyAction
): ImportReducerState => {
    const { type, ...rest } = action;
    switch (type) {
        case REQUEST_COMPUTE_ITEM_CHANGES:
        case REQUEST_RETRY_FAILED:
        case REQUEST_IMPORT_JOB_ITEMS:
        case REQUEST_IMPORT_DETAILS:
        case REQUEST_IMPORTS: {
            return {
                ...state,
                requesting: true,
            };
        }

        case RECEIVE_IMPORTS: {
            return {
                ...state,
                importJobs: action['imports'],
                requesting: false,
            };
        }

        case RECEIVE_IMPORT_DETAILS: {
            return {
                ...state,
                importDetails: {
                    ...rest['importDetails'],
                    shouldDeleteExisting: rest['importDetails']?.shouldDeleteExisting ?? false,
                    keepSpecialPrices: rest['importDetails']?.keepSpecialPrices ?? true,
                },
                requesting: false,
            };
        }

        case RECEIVE_IMPORT_JOB_ITEMS: {
            return {
                ...state,
                importJobItems: rest['importItems'],
                requesting: false,
            };
        }
        case RESET_IMPORTS: {
            return {
                ...state,
                importJobs: initialImportsReducerState.importJobs,
            };
        }

        case IMPORT_ABORTED: {
            const importDetails = state.importDetails;
            if (!importDetails) {
                return state;
            }

            return {
                ...state,
                importDetails: {
                    ...importDetails,
                    status: ImportStatus.Aborted,
                },
                importJobs: {
                    ...state.importJobs,
                    items: state.importJobs.items.map(job => {
                        if (job.id === rest['importId']) {
                            return {
                                ...job,
                                status: ImportStatus.Aborted,
                            };
                        }

                        return job;
                    }),
                },
            };
        }

        case DELETE_IMPORT: {
            const imports = [...state.importJobs.items];
            const index = imports.findIndex(item => item.id === rest['importId']);
            if (index >= 0) {
                imports.splice(index, 1);
            }

            return {
                ...state,
                importJobs: {
                    ...state.importJobs,
                    items: imports,
                },
            };
        }

        case REMOVE_ARTICLES_COMPLETED: {
            const items = state.importJobItems.items.filter(item => !rest['articleIds'].includes(item.id));

            return {
                ...state,
                importJobItems: {
                    ...state.importJobItems,
                    items,
                },
            };
        }

        case PUBLISH_ARTICLES_COMPLETED: {
            const items = state.importJobItems.items.filter(item => !rest['articleIds'].includes(item.id));

            return {
                ...state,
                importJobItems: {
                    ...state.importJobItems,
                    items,
                },
            };
        }

        case REQUEST_IMPORTS_END:
        case RETRY_FAILED_COMPLETED:
        case COMPUTE_ITEM_CHANGES_STARTED: {
            return {
                ...state,
                requesting: false,
            };
        }

        case IMPORT_CHANGE_SETTINGS: {
            const importDetails = state.importDetails;
            if (!importDetails) {
                return state;
            }

            return {
                ...state,
                importDetails: {
                    ...importDetails,
                    shouldDeleteExisting: rest['shouldDeleteExisting'] ?? importDetails.shouldDeleteExisting,
                    shouldSkipCategoryCreation:
                        rest['shouldSkipCategoryCreation'] ?? importDetails.shouldSkipCategoryCreation,
                    keepSpecialPrices: rest['keepSpecialPrices'] ?? importDetails.keepSpecialPrices,
                    shouldOverwriteFiles: rest['shouldOverwriteFiles'] ?? importDetails.shouldOverwriteFiles,
                    shouldNotifySupplier: rest['shouldNotifySupplier'] ?? importDetails.shouldNotifySupplier,
                },
            };
        }

        case IMPORT_CHANGE_SETTINGS_PUBLISH_DATE: {
            const importDetails = state.importDetails;
            if (!importDetails) {
                return state;
            }

            return {
                ...state,
                importDetails: {
                    ...importDetails,
                    publishDate: rest['publishDate'],
                    status: rest['publishDate'] ? ImportStatus.Scheduled : ImportStatus.ReadyToPublish,
                },
            };
        }

        default: {
            return state;
        }
    }
};
